import type { AIProfile, StyleId } from './types';

/** The five instincts that make up a creature's temperament. */
export type StyleTraits = Pick<AIProfile, 'aggression' | 'caution' | 'curiosity' | 'patience' | 'confidence'>;

/**
 * How a style chooses its standoff distance.
 *
 * `base` is the median range of the creature's damaging moves, so a style
 * expresses itself relative to whatever kit it happens to be paired with. Two
 * shapes are genuinely different and cannot reduce to one rule, which is why
 * this is a function rather than a set of coefficients:
 * a Survivor wants to be further away the closer it is to fainting.
 */
export type PreferredDistance = (base: number, hp: number) => number;

export interface StyleProfile {
  /** Display name used in the collection and result screens. */
  label: string;
  /** The instincts a creature of this style starts with, before any training. */
  traits: StyleTraits;
  /** Seconds between noticing a change in the fight and reacting to it. */
  reactionDelay: number;
  /** Distance in arena units this style tries to keep from its target. */
  preferredDistance: PreferredDistance;
  /**
   * HP percentage above which this style will not spend energy on a guard
   * ability. Six of the seven styles start protecting themselves at 78% HP;
   * a Brawler waits until it is genuinely threatened, so it keeps attacking
   * through the part of the fight where everything else goes quiet.
   */
  selfPreservationHp: number;
  /** How fast the circling angle advances, in radians per second. */
  orbitRate: number;
  /** Caution level above which this style retreats to arena corners, or 'always'. */
  addsCornersWhenCautionAbove: number | 'always';
  /**
   * How far training may move an instinct from this style's baseline.
   *
   * Training nudges instincts after every battle. Without a bound those nudges
   * accumulate without limit, so repeated losses would walk a Brawler's caution
   * up past a Hunter's and blur the archetype into the middle of the range.
   * Bounding each instinct to a window around an immutable baseline means a
   * creature keeps its identity no matter how much it trains.
   */
  trainingDrift: number;
}

/**
 * Every per-style behaviour in one place.
 *
 * This is the single source of truth: instincts, reaction speed, standoff,
 * self-preservation, orbit speed and corner seeking all live here, so adding a
 * style means adding one entry. `Record<StyleId, StyleProfile>` is exhaustive,
 * so a new style that omits a field fails the build instead of silently
 * inheriting another style's behaviour.
 *
 * Types only: this module must stay free of Phaser and of the DOM so it can be
 * loaded by headless scripts.
 */
export const styleProfiles: Record<StyleId, StyleProfile> = {
  hunter: {
    label: 'Hunter',
    traits: { aggression: 0.84, caution: 0.25, curiosity: 0.46, patience: 0.34, confidence: 0.8 },
    reactionDelay: 0.34,
    preferredDistance: (base) => Math.min(62, base * 0.55),
    selfPreservationHp: 78,
    orbitRate: 0.45,
    addsCornersWhenCautionAbove: 0.72,
    trainingDrift: 0.12,
  },
  skirmisher: {
    label: 'Skirmisher',
    traits: { aggression: 0.65, caution: 0.55, curiosity: 0.54, patience: 0.62, confidence: 0.67 },
    reactionDelay: 0.28,
    preferredDistance: (base) => Math.max(88, base * 0.9),
    selfPreservationHp: 78,
    orbitRate: 0.45,
    addsCornersWhenCautionAbove: 0.72,
    trainingDrift: 0.12,
  },
  guardian: {
    label: 'Guardian',
    traits: { aggression: 0.44, caution: 0.82, curiosity: 0.3, patience: 0.78, confidence: 0.58 },
    reactionDelay: 0.39,
    preferredDistance: (base) => Math.max(68, base * 0.72),
    selfPreservationHp: 78,
    orbitRate: 0.45,
    addsCornersWhenCautionAbove: 0.72,
    trainingDrift: 0.12,
  },
  trickster: {
    label: 'Trickster',
    traits: { aggression: 0.57, caution: 0.48, curiosity: 0.9, patience: 0.72, confidence: 0.61 },
    reactionDelay: 0.31,
    preferredDistance: (base) => Math.max(84, base * 0.84),
    selfPreservationHp: 78,
    orbitRate: 0.9,
    addsCornersWhenCautionAbove: 0.72,
    trainingDrift: 0.12,
  },
  ambusher: {
    label: 'Ambusher',
    traits: { aggression: 0.69, caution: 0.66, curiosity: 0.82, patience: 0.86, confidence: 0.63 },
    reactionDelay: 0.45,
    preferredDistance: (base) => Math.max(102, base),
    selfPreservationHp: 78,
    orbitRate: 0.45,
    addsCornersWhenCautionAbove: 'always',
    trainingDrift: 0.12,
  },
  survivor: {
    label: 'Survivor',
    traits: { aggression: 0.35, caution: 0.92, curiosity: 0.38, patience: 0.83, confidence: 0.42 },
    reactionDelay: 0.37,
    preferredDistance: (base, hp) => (hp < 55 ? Math.max(126, base) : Math.max(82, base * 0.8)),
    selfPreservationHp: 78,
    orbitRate: 0.45,
    addsCornersWhenCautionAbove: 0.72,
    trainingDrift: 0.12,
  },
  brawler: {
    label: 'Brawler',
    traits: { aggression: 0.88, caution: 0.14, curiosity: 0.12, patience: 0.15, confidence: 0.85 },
    reactionDelay: 0.3,
    preferredDistance: (base) => Math.min(48, base * 0.5),
    // The headline difference from every other style: a Brawler keeps trading
    // until it is genuinely threatened instead of guarding from 78% HP.
    selfPreservationHp: 45,
    orbitRate: 0.22,
    // Unchanged, so a Brawler never camps corners at its own low caution.
    addsCornersWhenCautionAbove: 0.72,
    trainingDrift: 0.12,
  },
};

/**
 * Per-style lookups built from the registry. `Object.fromEntries` cannot know
 * that every style is present, so the cast is what re-states the exhaustiveness
 * that `styleProfiles` already guarantees.
 */
const lookup = <T>(select: (profile: StyleProfile) => T): Record<StyleId, T> =>
  Object.fromEntries(Object.entries(styleProfiles).map(([style, profile]) => [style, select(profile)])) as Record<StyleId, T>;

/** Display labels, derived so a style only ever names itself in one place. */
export const styleLabels = lookup((profile) => profile.label);

/** Narrows an untrusted string, such as a URL parameter, to a style id. */
export const isStyleId = (value: unknown): value is StyleId => typeof value === 'string' && Object.hasOwn(styleProfiles, value);

/** Reaction delays, for callers that only need the delay. */
export const reactionDelayByStyle = lookup((profile) => profile.reactionDelay);

/** The lowest and highest value an instinct may reach, bounded around its style baseline. */
export function instinctWindow(style: StyleId, trait: keyof StyleTraits): { low: number; high: number } {
  const baseline = styleProfiles[style].traits[trait];
  const drift = styleProfiles[style].trainingDrift;
  return { low: Math.max(0.05, baseline - drift), high: Math.min(0.98, baseline + drift) };
}
